import {
  memo,
  useCallback,
  useEffect,
  useState,
  useSyncExternalStore,
} from "react";

import { useCart } from "@shopify/hydrogen-react";
import { type CartLine } from "@shopify/hydrogen-react/storefront-api-types";

import { useCartActiveProduct } from "@cart/CartActiveProductContext";
import { useAnalyticsEffect } from "@react/hooks/useAnalyticsEffect";
import { useCartAnalytics } from "@react/hooks/useCartAnalytics";
import { useSectionSettings } from "@react/sections/Root/SectionSettingsContext";
import { TablescapeContext, TablescapeEvent } from "@tablescape/context";
import {
  type TablescapeState,
  type CartSectionSettings,
  type CartAnalyticsLine,
} from "@tablescape/state";
import { getShopifyGlobalId } from "@tablescape/utilities";

const CartDrawerAnalyticsBase = () => {
  const {
    ui: { cartDrawerUserToggled },
  } = useSyncExternalStore<TablescapeState>(
    TablescapeContext.onAll,
    TablescapeContext.getSnapshot
  );
  const { activeProduct, activeUpsell } = useCartActiveProduct();
  const { checkoutUrl, cost, id, lines, status } = useCart();
  const {
    meta: { customer },
  } = useSectionSettings<CartSectionSettings>();
  const [linesToReportAnalytics, setLinesToReportAnalytics] = useState<
    | {
        bundleProduct:
          | {
              compareAtPrice: number;
              id: string;
              imageURL: string;
              price: number;
              title: string;
            }
          | undefined;
        lines: CartAnalyticsLine[];
      }
    | undefined
  >(undefined);

  const {
    reportAddToCartEvent,
    reportCartViewedEvent,
    reportUpsellAddToCartEvent,
  } = useCartAnalytics();

  useAnalyticsEffect(() => {
    if (!cartDrawerUserToggled) {
      return;
    }

    reportCartViewedEvent({
      checkoutUrl,
      cost,
      customer,
      id,
      lines,
    });
  }, [cartDrawerUserToggled]);

  useAnalyticsEffect(() => {
    if (
      status !== "idle" ||
      !lines?.length ||
      !linesToReportAnalytics?.lines?.length
    ) {
      return;
    }

    const validLines = linesToReportAnalytics?.lines
      ?.map((addedLine) =>
        lines.find((line) => line?.merchandise?.id === addedLine?.merchandiseId)
      )
      .filter(Boolean);

    if (!validLines?.length) {
      return;
    }

    reportAddToCartEvent({
      ...linesToReportAnalytics,
      checkoutUrl,
      cost,
      lines: validLines,
    });

    if (activeProduct && activeUpsell) {
      reportUpsellAddToCartEvent({ customer, id, lines, ...activeUpsell });
    }

    setLinesToReportAnalytics(undefined);
  }, [linesToReportAnalytics, status, lines]);

  const handleAddToCart = useCallback((data?: TablescapeState) => {
    const bundleProduct = data?.cart?.bundleProduct;
    const lines = data?.cart?.lines;

    if (!Array.isArray(lines)) {
      return;
    }

    const linesToAdd = lines
      .filter((line): line is CartLine => !!(line && line?.id))
      .map((line) => ({
        merchandiseId: getShopifyGlobalId("ProductVariant", line.id),
        quantity: line.quantity,
      }));

    setLinesToReportAnalytics({
      bundleProduct,
      lines: linesToAdd,
    });
  }, []);

  useEffect(() => {
    const addToCartListener = TablescapeContext.on(
      TablescapeEvent.ADD_TO_CART,
      handleAddToCart
    );
    const addBundleToCartListener = TablescapeContext.on(
      TablescapeEvent.ADD_BUNDLE_TO_CART,
      handleAddToCart
    );

    return () => {
      addToCartListener.off(TablescapeEvent.ADD_TO_CART, handleAddToCart);
      addBundleToCartListener.off(
        TablescapeEvent.ADD_BUNDLE_TO_CART,
        handleAddToCart
      );
    };
  }, [handleAddToCart]);

  return null;
};

export const CartDrawerAnalytics = memo(CartDrawerAnalyticsBase);
